import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  isMobileBreakpoint,
  clamp,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Keys that move to the previous or next media */
const MEDIA_STEP_KEYS = /** @type {Record<string, number>} */ ({
  ArrowLeft: -1,
  ArrowUp: -1,
  PageUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
  PageDown: 1,
});

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [gallery] - The list of zoomed media.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {boolean} */
  #openedFromZoomTrigger = false;

  /** @type {Map<Element, number>} */
  #mediaRatios = new Map();

  /** The media a thumbnail or key is scrolling to, the media passed on the way don't take the current thumbnail */
  #targetIndex = -1;

  // Follows the media in view while the dialog is open, so the current thumbnail changes as soon as a swipe passes halfway
  #mediaObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) this.#mediaRatios.set(entry.target, entry.intersectionRatio);
      this.#followMediaInView();
    },
    { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
  );

  // The dialog scrolls vertically on desktop, the gallery list horizontally on mobile
  get #scrollContainers() {
    return [this.refs.dialog, this.refs.gallery];
  }

  // The current thumbnail follows the media in view
  get #currentIndex() {
    const buttons = Array.from(this.refs.thumbnails.querySelectorAll('button'));
    return Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-current') === 'true'));
  }

  get #mostVisibleIndex() {
    let mostVisibleIndex = -1;
    let mostVisibleRatio = 0;

    this.refs.media.forEach((element, index) => {
      const ratio = this.#mediaRatios.get(element) ?? 0;
      if (ratio <= mostVisibleRatio) return;
      mostVisibleIndex = index;
      mostVisibleRatio = ratio;
    });

    return mostVisibleIndex;
  }

  /**
   * @param {number} index - The index of the current media.
   */
  #setCurrentThumbnail(index) {
    Array.from(this.refs.thumbnails.querySelectorAll('button')).forEach((button, i) => {
      if (i === index) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    });
  }

  connectedCallback() {
    super.connectedCallback();
    for (const container of this.#scrollContainers) container?.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const container of this.#scrollContainers) container?.removeEventListener('scroll', this.handleScroll);
    this.#mediaObserver.disconnect();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { activeElement } = document;
    this.#openedFromZoomTrigger =
      activeElement instanceof Element && activeElement.classList.contains('product-media__zoom-trigger');

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const element of media) this.#mediaObserver.observe(element);

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Swaps in the full resolution image once the shopper magnifies a media.
   * Until then the srcset file sized for the fitted image is enough.
   * @param {Element | null} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.classList.contains('product-media-container--image')) return;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return;

    this.#highResImagesLoaded.add(highResolutionUrl);

    // Skip the swap when srcset already picked a file close to the largest one
    const srcsetWidths = image.srcset.split(',').map((candidate) => parseInt(candidate.trim().split(/\s+/)[1] ?? '', 10));
    const largestWidth = Math.max(0, ...srcsetWidths.filter(Boolean));
    const currentWidth = image.currentSrc ? Number(new URL(image.currentSrc).searchParams.get('width')) : 0;
    if (currentWidth * 1.25 >= largestWidth) return;

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.src = highResolutionUrl;

    // Decode before swapping so the image doesn't flash blank while magnified
    newImage
      .decode()
      .then(() => image.replaceWith(newImage))
      .catch(() => this.#highResImagesLoaded.delete(highResolutionUrl));
  }

  /**
   * Makes the most visible media the current one and scrolls its thumbnail into view,
   * unless a thumbnail or key is still scrolling to another media.
   */
  #followMediaInView() {
    const { thumbnails } = this.refs;
    const activeIndex = this.#mostVisibleIndex;

    // No media is visible once the dialog closes
    if (activeIndex === -1) return;

    if (this.#targetIndex !== -1) {
      if (activeIndex !== this.#targetIndex) return;
      this.#targetIndex = -1;
    }

    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;
    if (targetThumbnail.getAttribute('aria-current') === 'true') return;

    this.#setCurrentThumbnail(activeIndex);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Once scrolling stops, the media in view takes the current thumbnail,
   * even when a swipe interrupted the scroll to the media of a thumbnail or key.
   */
  handleScroll = debounce(() => {
    this.#targetIndex = -1;
    this.#followMediaInView();
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);

    // The gallery behind the dialog only follows along on close, so browsing the zoom doesn't download its slides
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    this.#mediaObserver.disconnect();
    this.#mediaRatios.clear();
    this.#targetIndex = -1;
    window.dispatchEvent(new DialogCloseEvent());
    this.#focusCurrentZoomTrigger();
  }

  /**
   * The dialog returns focus to the button that opened it, but the gallery now shows the media that was zoomed last,
   * so the button of that media takes the focus instead.
   */
  #focusCurrentZoomTrigger() {
    if (!this.#openedFromZoomTrigger) return;
    this.#openedFromZoomTrigger = false;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('media-gallery'));
    const slide = mediaGallery?.slideshow?.slides?.[this.#currentIndex];
    const zoomTrigger = slide?.querySelector('.product-media__zoom-trigger');

    if (zoomTrigger instanceof HTMLElement) zoomTrigger.focus();
  }

  /**
   * Closes the dialog when the space around an image is clicked on desktop.
   * A click on the image itself magnifies it in drag-zoom-wrapper.js.
   * On mobile a tap does nothing, so double tap can magnify the image.
   * @param {MouseEvent} event
   */
  handleMediaClick(event) {
    if (isMobileBreakpoint()) return;

    // The event target is proxied to the element with the handler, the composed path has the clicked element
    if (event.composedPath()[0] instanceof HTMLImageElement) return;

    this.close();
  }

  /**
   * Closes the dialog when the user presses the escape key.
   * The arrow, Page Up, Page Down, Home and End keys move between media.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    // Videos and 3D models use these keys for their own controls
    const target = event.composedPath()[0];
    if (target instanceof Element && target.closest('video, model-viewer')) return;

    const lastIndex = this.refs.media.length - 1;
    const step = MEDIA_STEP_KEYS[event.key];
    let index;

    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = lastIndex;
    else if (step) index = clamp(this.#currentIndex + step, 0, lastIndex);
    else return;

    event.preventDefault();
    this.selectThumbnail(index, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#setCurrentThumbnail(index);
    this.#targetIndex = index === this.#mostVisibleIndex ? -1 : index;

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    media[index]?.scrollIntoView({ behavior: options.behavior });
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
